import React, { lazy, Suspense } from "react";
import { sessionTenant } from "@/lib/session";
import "@/App.css";
import { BrowserRouter, Routes, Route, Outlet } from "react-router-dom";
import { Toaster } from "sonner";
// Foundation SaaS marketing site (the always-Foundation shell)
import AgencyAbout from "@/pages/AgencyAbout";
import AgencyContact from "@/pages/AgencyContact";
import AgencySell from "@/pages/AgencySell";
import AgencyLet from "@/pages/AgencyLet";
import AgencyGuides from "@/pages/AgencyGuides";
import AgencyGuideDetail from "@/pages/AgencyGuideDetail";
import AgencyLegal from "@/pages/AgencyLegal";
import AgencyFees from "@/pages/AgencyFees";

// Tenant-branded surface (uses config/brand.json / /api/brand)
import Landing from "@/pages/Landing";
import AgencySite from "@/pages/AgencySite";
import PublicListings from "@/pages/PublicListings";
import AreaLandingPage from "@/pages/AreaLandingPage";
import AreaPricesPage from "@/pages/AreaPricesPage";
import PublicListingDetail from "@/pages/PublicListingDetail";
import { BrandProvider, TenantScope, useBrand, useMarket } from "@/lib/brand";
import { VisitInterestProvider } from "@/lib/visitInterest";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import ScrollToTop from "@/components/ScrollToTop";
import PageviewTracker from "@/components/PageviewTracker";
import WebChat from "@/components/WebChat";

// ---------------------------------------------------------------------------
// Route-level code splitting.
//
// Every page in the product used to arrive in one bundle — 463KB gzipped,
// 1.8MB raw — so a homeowner opening a valuation form on an agency's website
// downloaded the entire CRM, the admin console, the client portal and
// Foundation's own marketing site before anything could render.
//
// The public tenant surface (home, valuation, listings, area and the agency
// content pages) stays eagerly imported: it is the fast path, it is what
// Google lands on, and splitting it would only trade bytes for a round trip.
// Everything behind a login, and everything belonging to Foundation rather
// than to the agency, is loaded on demand.
// ---------------------------------------------------------------------------
// The valuation form. Eager once, because it is a lead-capture page and a
// landing page in its own right — but it is 60KB of source, it draws the
// heaviest of the shadcn controls, and it is on no other page's critical
// path. Every visitor to an agency's home page was downloading it.
const Valuation = lazy(() => import("@/pages/Valuation"));
// The 404 page, which draws Foundation's own marketing chrome — and so
// dragged FoundationLayout and components/foundation/Brand into the entry
// bundle, on every page of every agency's website, for a page almost nobody
// is served.
const NotFound = lazy(() => import("@/pages/NotFound"));
const FoundationHome = lazy(() => import("@/pages/FoundationHome"));
const FoundationPricing = lazy(() => import("@/pages/FoundationPricing"));
const FoundationPlatform = lazy(() => import("@/pages/FoundationPlatform"));
const FoundationSeoPage = lazy(() => import("@/pages/FoundationSeoPage"));
const FoundationCaseStudy = lazy(() => import("@/pages/FoundationCaseStudy"));
const FoundationDemo = lazy(() => import("@/pages/FoundationDemo"));
// The 2026 design system, rendered. Linked from nowhere and reachable only
// by typing the path: it is the repo's stand-in for Storybook, which it
// does not have. See docs/ux-overhaul/AUDIT.md.
const F2026Gallery = lazy(() => import("@/pages/F2026Gallery"));
const FoundationCheckout = lazy(() => import("@/pages/FoundationCheckout"));
const FoundationValuationProduct = lazy(() => import("@/pages/FoundationValuationProduct"));
const FoundationReportsProduct = lazy(() => import("@/pages/FoundationReportsProduct"));
const FoundationSocialProduct = lazy(() => import("@/pages/FoundationSocialProduct"));
const FoundationCrmProduct = lazy(() => import("@/pages/FoundationCrmProduct"));
const FoundationBlog = lazy(() => import("@/pages/FoundationBlog"));
const FoundationBlogPost = lazy(() => import("@/pages/FoundationBlogPost"));
const FoundationLettingsProduct = lazy(() => import("@/pages/FoundationLettingsProduct"));
const FoundationSitesProduct = lazy(() => import("@/pages/FoundationSitesProduct"));
const FoundationStudioProduct = lazy(() => import("@/pages/FoundationStudioProduct"));
const FoundationReachProduct = lazy(() => import("@/pages/FoundationReachProduct"));
const FoundationSignalProduct = lazy(() => import("@/pages/FoundationSignalProduct"));
const FoundationGuidesProduct = lazy(() => import("@/pages/FoundationGuidesProduct"));
const FoundationSkills = lazy(() => import("@/pages/FoundationSkills"));
const FoundationConnectProduct = lazy(() => import("@/pages/FoundationConnectProduct"));
const FoundationSequencesProduct = lazy(() => import("@/pages/FoundationSequencesProduct"));
const FoundationLegal = lazy(() => import("@/pages/FoundationLegal"));
const FoundationAdminLogin = lazy(() => import("@/pages/FoundationAdmin").then((m) => ({ default: m.FoundationAdminLogin })));
const FoundationAdminDashboard = lazy(() => import("@/pages/FoundationAdmin").then((m) => ({ default: m.FoundationAdminDashboard })));
const PaymentSuccess = lazy(() => import("@/pages/PaymentSuccess"));
const PaymentCancel = lazy(() => import("@/pages/PaymentCancel"));
const SignPage = lazy(() => import("@/pages/SignPage"));
const PortalLogin = lazy(() => import("@/pages/portal/PortalLogin"));
const PortalConsume = lazy(() => import("@/pages/portal/PortalConsume"));
const PortalDashboard = lazy(() => import("@/pages/portal/PortalDashboard"));
const CmaRequest = lazy(() => import("@/pages/CmaRequest"));
const MarketReport = lazy(() => import("@/pages/MarketReport"));
const RegisterApplicant = lazy(() => import("@/pages/RegisterApplicant"));
const CalculatorsIndex = lazy(() => import("@/pages/calculators/CalculatorsIndex"));
const StampDutyCalculator = lazy(() => import("@/pages/calculators/StampDutyCalculator"));
const MortgageCalculator = lazy(() => import("@/pages/calculators/MortgageCalculator"));
const YieldCalculator = lazy(() => import("@/pages/calculators/YieldCalculator"));
const BtlRoiCalculator = lazy(() => import("@/pages/calculators/BtlRoiCalculator"));
const MovingCostsCalculator = lazy(() => import("@/pages/calculators/MovingCostsCalculator"));
const AdminLogin = lazy(() => import("@/pages/AdminLogin"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const CrmDemoEntry = lazy(() => import("@/pages/CrmDemoEntry"));
const AcceptInvite = lazy(() => import("@/pages/AcceptInvite"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const AdminBrand = lazy(() => import("@/pages/AdminBrand"));
const AdminHelp = lazy(() => import("@/pages/AdminHelp"));
const CrmLayout = lazy(() => import("@/pages/crm/CrmLayout"));

// The Listings Manager — the back office for an agency whose product is
// their website. Its own route space rather than a corner of /crm, because
// it is not the CRM: see pages/manage/ManageLayout.jsx.
const ManageLayout = lazy(() => import("@/pages/manage/ManageLayout"));
const ManageProperties = lazy(() => import("@/pages/manage/ManageProperties"));
const ManagePropertyEditor = lazy(() => import("@/pages/manage/ManagePropertyEditor"));
const ManageEnquiries = lazy(() => import("@/pages/manage/ManageEnquiries"));
const CrmEnquiries = lazy(() => import("@/pages/crm/CrmEnquiries"));
const CrmKeys = lazy(() => import("@/pages/crm/CrmKeys"));
const CrmMaintenance = lazy(() => import("@/pages/crm/CrmMaintenance"));
const CrmReferrals = lazy(() => import("@/pages/crm/CrmReferrals"));
const CrmReviews = lazy(() => import("@/pages/crm/CrmReviews"));
const CrmBoards = lazy(() => import("@/pages/crm/CrmBoards"));
const CrmMileage = lazy(() => import("@/pages/crm/CrmMileage"));
const CrmTeamBoardScreen = lazy(() => import("@/pages/crm/CrmTeamBoardScreen"));
const BookAppointment = lazy(() => import("@/pages/BookAppointment"));
const ManageBooking = lazy(() => import("@/pages/ManageBooking"));
const ManageBuyers = lazy(() => import("@/pages/manage/ManageBuyers"));
const ManageValuations = lazy(() => import("@/pages/manage/ManageValuations"));
const ManageFeeds = lazy(() => import("@/pages/manage/ManageFeeds"));
const CrmOverview = lazy(() => import("@/pages/crm/CrmOverview"));
const CrmContacts = lazy(() => import("@/pages/crm/CrmContacts"));
const CrmContactDetail = lazy(() => import("@/pages/crm/CrmContactDetail"));
const CrmProperties = lazy(() => import("@/pages/crm/CrmProperties"));
const CrmPropertyDetail = lazy(() => import("@/pages/crm/CrmPropertyDetail"));
const AgreementSignPage = lazy(() => import("@/pages/AgreementSignPage"));
const CrmAgencyTerms = lazy(() => import("@/pages/crm/CrmAgencyTerms"));
// The pattern workshop. Not a product screen: it renders the shared
// components in every state so they can be checked against the design
// without hunting for a property that happens to have a failed feed.
const DesignStates = lazy(() => import("@/pages/crm/DesignStates"));
// Screens made of screens: nine destinations moved down a level in the
// redesign and now share a parent. See lib/crmNav.js for the whole map.
const CrmSettings = lazy(() => import("@/pages/crm/CrmSettings"));
const CrmWebsite = lazy(() => import("@/pages/crm/CrmWebsite"));
const CrmTasksScreen = lazy(() => import("@/pages/crm/CrmTasksScreen"));
const CrmLettingsScreen = lazy(() => import("@/pages/crm/CrmLettingsScreen"));
const MovedRoute = lazy(() => import("@/pages/crm/MovedRoute"));
const CrmDocumentBrand = lazy(() => import("@/pages/crm/CrmDocumentBrand"));
const CrmOffers = lazy(() => import("@/pages/crm/CrmOffers"));
const CrmTransactions = lazy(() => import("@/pages/crm/CrmTransactions"));
const CrmGoals = lazy(() => import("@/pages/crm/CrmGoals"));
const CrmSmartLists = lazy(() => import("@/pages/crm/CrmSmartLists"));
const CrmSpeedToLead = lazy(() => import("@/pages/crm/CrmSpeedToLead"));
const CrmOpenHouses = lazy(() => import("@/pages/crm/CrmOpenHouses"));
const CrmSellerReport = lazy(() => import("@/pages/crm/CrmSellerReport"));
// A seller's own view of their own sale. Public and unauthenticated:
// the link is the credential.
const VendorPortal = lazy(() => import("@/pages/VendorPortal"));
const BuyerPortal = lazy(() => import("@/pages/BuyerPortal"));
const TenantPortal = lazy(() => import("@/pages/TenantPortal"));
const CrmStateRules = lazy(() => import("@/pages/crm/CrmStateRules"));
const CrmViewings = lazy(() => import("@/pages/crm/CrmViewings"));
const CrmTasks = lazy(() => import("@/pages/crm/CrmTasks"));
const CrmReports = lazy(() => import("@/pages/crm/CrmReports"));
const CrmArrears = lazy(() => import("@/pages/crm/CrmArrears"));
const CrmSocial = lazy(() => import("@/pages/crm/CrmSocial"));
const CrmEmails = lazy(() => import("@/pages/crm/CrmEmails"));
const CrmLettings = lazy(() => import("@/pages/crm/CrmLettings"));
const CrmTeam = lazy(() => import("@/pages/crm/CrmTeam"));
const CrmTemplates = lazy(() => import("@/pages/crm/CrmTemplates"));
const CrmGuides = lazy(() => import("@/pages/crm/CrmGuides"));
const CrmObjections = lazy(() => import("@/pages/crm/CrmObjections"));
const CrmSequences = lazy(() => import("@/pages/crm/CrmSequences"));
const CrmBranches = lazy(() => import("@/pages/crm/CrmBranches"));
const CrmReach = lazy(() => import("@/pages/crm/CrmReach"));
const CrmSignal = lazy(() => import("@/pages/crm/CrmSignal"));
const CrmWhatsApp = lazy(() => import("@/pages/crm/CrmWhatsApp"));
const CrmOfficeInbox = lazy(() => import("@/pages/crm/CrmOfficeInbox"));
const FoundationDataDeletion = lazy(() => import("@/pages/FoundationDataDeletion"));
const CrmSkills = lazy(() => import("@/pages/crm/CrmSkills"));
const CrmCanvass = lazy(() => import("@/pages/crm/CrmCanvass"));
const CrmAsk = lazy(() => import("@/pages/crm/CrmAsk"));
const CrmAccounts = lazy(() => import("@/pages/crm/CrmAccounts"));
const CrmNrl = lazy(() => import("@/pages/crm/CrmNrl"));
const CrmInvoices = lazy(() => import("@/pages/crm/CrmInvoices"));
const CrmAppraisals = lazy(() => import("@/pages/crm/CrmAppraisals"));
const CrmInventory = lazy(() => import("@/pages/crm/CrmInventory"));
const CrmInventoryDetail = lazy(() => import("@/pages/crm/CrmInventoryDetail"));
const CrmCalendar = lazy(() => import("@/pages/crm/CrmCalendar"));
const CrmExports = lazy(() => import("@/pages/crm/CrmExports"));
const FoundationTerms = lazy(() => import("@/pages/FoundationTerms"));
const FoundationSubProcessors = lazy(() => import("@/pages/FoundationSubProcessors"));
const AccountExport = lazy(() => import("@/pages/AccountExport"));


/**
 * Everything inside the router.
 *
 * Split out so the SERVER can supply a router too. renderToString has no
 * history and no document, so `BrowserRouter` throws there; the SSR entry
 * wraps this in a StaticRouter at the requested URL instead. Both render a
 * context and no DOM of their own, so the tree React hydrates on the client
 * is the tree the server produced.
 */
export function AppRoutes() {
  return (
    <>
      <ScrollToTop />
      {/* What this visitor is looking at, for THIS VISIT, held in memory and
          never written to the device. It is what lets the home page lead with
          the area they have just been reading about. See lib/visitInterest for
          why it is not a tracker and must not become one.

          IT WRAPS EVERYTHING, and that is the point. It used to sit inside
          TenantRoutes, which only serves /t/:slug/* — the preview path. Every
          paying agency is on its own domain, where the home page and the area
          guides are routed from the group below, outside it. So useVisitInterest
          returned the context DEFAULT on every real site: noteArea a no-op,
          topArea forever null, and a feature that worked only where nobody
          looked at it. */}
      <VisitInterestProvider>
      {/* One boundary around the whole table. The fallback is deliberately
          blank rather than a spinner: the eagerly-bundled public pages never
          suspend, so this only ever shows while a lazily-loaded chunk is in
          flight, and a flashed spinner on a fast connection reads as jank. */}
      <Suspense fallback={<div style={{ minHeight: "60vh" }} />}>
      <Routes>
        {/* Foundation marketing site — always renders the Foundation brand */}
        <Route element={<AppShell />}>
          <Route path="/" element={<BrandedOrFoundation tenant={<AgencySite />} foundation={<FoundationHome />} />} />
          <Route path="/vendor/:token" element={<VendorPortal />} />
          <Route path="/buyer/:token" element={<BuyerPortal />} />
          <Route path="/tenant/:token" element={<TenantPortal />} />
          <Route path="/pricing" element={<FoundationPricing />} />
          <Route path="/blog" element={<FoundationBlog />} />
          <Route path="/blog/:slug" element={<FoundationBlogPost />} />
          <Route path="/demo" element={<FoundationDemo />} />
          <Route path="/f2026" element={<F2026Gallery />} />
          <Route path="/product/valuation" element={<FoundationValuationProduct />} />
          <Route path="/product/reports" element={<FoundationReportsProduct />} />
          <Route path="/product/social" element={<FoundationSocialProduct />} />
          <Route path="/product/crm" element={<FoundationCrmProduct />} />
          <Route path="/product/signal" element={<FoundationSignalProduct />} />
          <Route path="/product/guides" element={<FoundationGuidesProduct />} />
          <Route path="/product/lettings" element={<FoundationLettingsProduct />} />
          <Route path="/product/sites" element={<FoundationSitesProduct />} />
          <Route path="/product/studio" element={<FoundationStudioProduct />} />
          <Route path="/product/reach" element={<FoundationReachProduct />} />
          <Route path="/product/connect" element={<FoundationConnectProduct />} />
          <Route path="/product/follow-ups" element={<FoundationSequencesProduct />} />
          {/* Legacy paths — old marketing links kept working */}
          <Route path="/product/valuer" element={<FoundationValuationProduct />} />
          <Route path="/product/sequences" element={<FoundationSequencesProduct />} />
          <Route path="/checkout/:lookup_key" element={<FoundationCheckout />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route path="/about" element={<BrandedOrFoundation tenant={<AgencyAbout />} foundation={<FoundationLegal kind="about" />} />} />
          <Route path="/platform" element={<FoundationPlatform />} />
          <Route path="/estate-agent-crm" element={<FoundationSeoPage slug="estate-agent-crm" />} />
          <Route path="/letting-agent-software" element={<FoundationSeoPage slug="letting-agent-software" />} />
          <Route path="/case-study" element={<FoundationCaseStudy />} />
          {/* Not under /product/: the eleven jobs run ACROSS the products,
              and the site states its product count as ten in eight places. */}
          <Route path="/skills" element={<FoundationSkills />} />
          <Route path="/legal/privacy" element={<FoundationLegal kind="privacy" />} />
          {/* The real Platform Terms, served from backend/legal/terms/. NOT
              at /terms — that path is the AGENCY's own terms page on their
              white-label site, and putting Foundation's contract there would
              replace every customer's. `/legal/terms/v/:version` is the
              frozen address a signed acceptance can point at forever. */}
          <Route path="/legal/terms" element={<FoundationTerms />} />
          <Route path="/legal/terms/v/:version" element={<FoundationTerms />} />
          <Route path="/legal/sub-processors" element={<FoundationSubProcessors />} />
          <Route path="/legal/cookies" element={<FoundationLegal kind="cookies" />} />
          {/* Meta REQUIRES this URL before App Review and tests that it
              answers. The data-deletion callback replies with a code and
              this address; without the route it served the app shell for an
              unrouted path, which is a blank page with a 200 on it. */}
          <Route path="/legal/data-deletion" element={<FoundationDataDeletion />} />

          {/* Public e-signature page — tokenised URL, no auth. */}
          <Route path="/sign/:token" element={<SignPage />} />
          {/* Agency agreements sign on their own path: the token is a
              different shape from the document one above, so sharing a
              route would mean guessing which format arrived. */}
          <Route path="/agreement/:token" element={<AgreementSignPage />} />

          {/* Foundation team super-admin (business dashboard) */}
          {/* Clause 9.2's export window. Outside the CRM shell on purpose:
              the account it exists for is the one the CRM answers 402 to. */}
          <Route path="/admin/export" element={<AccountExport />} />
          <Route path="/foundation-admin/login" element={<FoundationAdminLogin />} />
          <Route path="/foundation-admin" element={<FoundationAdminDashboard />} />

          {/* Legacy tenant-branded routes (resolve to DEFAULT_TENANT_SLUG — Foundation's own demo tenant, never a customer's) */}
          <Route path="/try" element={<Landing />} />
          <Route path="/valuation" element={<ValuationRoute />} />
          <Route path="/market-report" element={<MarketReport />} />
          <Route path="/embed" element={<Valuation />} />
          <Route path="/register" element={<RegisterApplicant />} />
          <Route path="/embed/register" element={<RegisterApplicant />} />
          <Route path="/embed/listings" element={<PublicListings />} />
          <Route path="/properties" element={<PublicListings />} />
          <Route path="/properties/:id" element={<PublicListingDetail />} />
          <Route path="/contact" element={<AgencyContact />} />
          <Route path="/sell" element={<AgencySell />} />
          <Route path="/let" element={<AgencyLet />} />
          <Route path="/guides" element={<AgencyGuides />} />
          <Route path="/guides/:guideSlug" element={<AgencyGuideDetail />} />
          <Route path="/area/:area" element={<AreaLandingPage />} />
          <Route path="/area/:area/house-prices" element={<AreaPricesPage />} />
          <Route path="/portal" element={<PortalLogin />} />
          <Route path="/portal/consume/:token" element={<PortalConsume />} />
          <Route path="/portal/dashboard" element={<PortalDashboard />} />
          <Route path="/fees" element={<AgencyFees />} />
          <Route path="/terms" element={<AgencyLegal kind="terms" />} />
          <Route path="/privacy" element={<AgencyLegal kind="privacy" />} />
          <Route path="/cookies" element={<AgencyLegal kind="cookies" />} />

          {/* Foundation Studio — free UK property calculators (always Foundation branded) */}
          <Route path="/calculators" element={<CalculatorsIndex />} />
          <Route path="/calculators/stamp-duty" element={<StampDutyCalculator />} />
          <Route path="/calculators/mortgage" element={<MortgageCalculator />} />
          <Route path="/calculators/yield" element={<YieldCalculator />} />
          <Route path="/calculators/btl-roi" element={<BtlRoiCalculator />} />
          <Route path="/calculators/moving-costs" element={<MovingCostsCalculator />} />

          {/* Admin — email-scoped tenant login. Wrapped in its own
              BrandProvider that reads tenant_slug from the login-response
              stashed in localStorage so the admin chrome (logo, palette,
              phone) matches the signed-in agency, not the default. */}
          <Route
            path="/admin/login"
            element={<AdminScoped><AdminLogin /></AdminScoped>}
          />
          {/* Self-service password reset — pre-auth, standalone (no
              AdminScoped, same as accept-invite): request a link, then set
              a new password from the emailed token. */}
          <Route path="/admin/forgot-password" element={<ForgotPassword />} />
          <Route path="/admin/reset-password" element={<ResetPassword />} />
          <Route path="/accept-invite/:token" element={<AcceptInvite />} />
          {/* Public, password-less entry into the self-serve CRM demo —
              silently logs in against the demo_mode-flagged `foundation`
              tenant (see server.py's /demo/crm-login) and drops the
              visitor straight into /crm. No login form. */}
          <Route path="/crm-demo" element={<CrmDemoEntry />} />
          <Route
            path="/admin"
            element={<AdminScoped><AdminDashboard /></AdminScoped>}
          />
          <Route
            path="/admin/brand"
            element={<AdminScoped><AdminBrand /></AdminScoped>}
          />
          <Route
            path="/admin/help"
            element={<AdminScoped><AdminHelp /></AdminScoped>}
          />

          {/* The Listings Manager. Same auth and the same BrandProvider as
              /crm, because it is the same account looking at the other half
              of what they bought. */}
          <Route path="/manage" element={<AdminScoped><ManageLayout /></AdminScoped>}>
            <Route index element={<ManageProperties />} />
            <Route path="properties/:id" element={<ManagePropertyEditor />} />
            <Route path="enquiries" element={<ManageEnquiries />} />
            <Route path="buyers" element={<ManageBuyers />} />
            <Route path="valuations" element={<ManageValuations />} />
            {/* The guides page of their own site. CrmGuides is mounted
                here rather than copied: it takes nothing from CrmLayout,
                so a website customer with no CRM edits the same articles
                through the same editor a Signature customer does. */}
            <Route path="guides" element={<CrmGuides />} />
            <Route path="feeds" element={<ManageFeeds />} />
          </Route>

          {/* Foundation CRM — full standalone module. Same auth as /admin
              so the tenant-scoped BrandProvider styles it correctly. */}
          <Route path="/crm" element={<AdminScoped><CrmLayout /></AdminScoped>}>
            <Route index element={<CrmOverview />} />
            <Route path="contacts" element={<CrmContacts />} />
            <Route path="contacts/:id" element={<CrmContactDetail />} />
            <Route path="whatsapp" element={<CrmWhatsApp />} />
            <Route path="office-inbox" element={<CrmOfficeInbox />} />
            <Route path="properties" element={<CrmProperties />} />
            <Route path="properties/:id" element={<CrmPropertyDetail />} />
            <Route path="offers" element={<CrmOffers />} />
            <Route path="transactions" element={<CrmTransactions />} />
            <Route path="goals" element={<CrmGoals />} />
            <Route path="smart-lists" element={<CrmSmartLists />} />
            <Route path="speed-to-lead" element={<CrmSpeedToLead />} />
            <Route path="open-houses" element={<CrmOpenHouses />} />
            <Route path="seller-report" element={<CrmSellerReport />} />
            <Route path="state" element={<CrmStateRules />} />
            <Route path="viewings" element={<CrmViewings />} />
            <Route path="tasks" element={<CrmTasksScreen />} />
            <Route path="lettings" element={<CrmLettingsScreen />} />
            <Route path="website" element={<CrmWebsite />} />
            <Route path="settings" element={<CrmSettings />} />
            <Route path="portals" element={<CrmExports />} />
            <Route path="signal" element={<CrmSignal />} />
            <Route path="skills" element={<CrmSkills />} />
            <Route path="canvass" element={<CrmCanvass />} />
            <Route path="ask" element={<CrmAsk />} />

            {/* Moved, not removed. Every one of these URLs is in somebody's
                bookmarks or in an address-bar autocomplete; the screen is one
                level down now and the old link still lands on it. */}
            <Route path="team" element={<MovedRoute to="/crm/settings#team" />} />
            <Route path="templates" element={<MovedRoute to="/crm/settings#templates" />} />
            <Route path="branches" element={<MovedRoute to="/crm/settings#branches" />} />
            <Route path="terms" element={<MovedRoute to="/crm/settings#terms" />} />
            <Route path="documents" element={<MovedRoute to="/crm/settings#documents" />} />
            <Route path="brand" element={<MovedRoute to="/crm/settings#brand" />} />
            <Route path="guides" element={<MovedRoute to="/crm/website#guides" />} />
            <Route path="reach" element={<MovedRoute to="/crm/website#search" />} />
            <Route path="follow-ups" element={<MovedRoute to="/crm/tasks#follow-ups" />} />
            <Route path="sequences" element={<MovedRoute to="/crm/tasks#follow-ups" />} />
            <Route path="exports" element={<MovedRoute to="/crm/portals" />} />
            {/* The LIST of inventories folded into Lettings; a single
                inventory keeps its own URL, because that one is a document
                an agent opens from an email. */}
            <Route path="inventory" element={<MovedRoute to="/crm/lettings#inventories" />} />
            <Route path="accounts" element={<CrmAccounts />} />
            <Route path="nrl" element={<CrmNrl />} />
            <Route path="invoices" element={<CrmInvoices />} />
            <Route path="appraisals" element={<CrmAppraisals />} />
            <Route path="objections" element={<CrmObjections />} />
            <Route path="calendar" element={<CrmCalendar />} />
            <Route path="inventory/:id" element={<CrmInventoryDetail />} />
            <Route path="social" element={<CrmSocial />} />
            <Route path="emails" element={<CrmEmails />} />
            <Route path="reports" element={<CrmReports />} />
            <Route path="arrears" element={<CrmArrears />} />
            {/* Brand settings + valuation leads, nested here (not under
                /admin) so the CRM's left sidebar stays mounted and
                accessible on every page, not just the main CRM sections. */}
            <Route path="design/states" element={<DesignStates />} />
            <Route path="enquiries" element={<CrmEnquiries />} />
            <Route path="keys" element={<CrmKeys />} />
            <Route path="maintenance" element={<CrmMaintenance />} />
            <Route path="referrals" element={<CrmReferrals />} />
            <Route path="reviews" element={<CrmReviews />} />
            <Route path="boards" element={<CrmBoards />} />
            <Route path="mileage" element={<CrmMileage />} />
            <Route path="team-board" element={<CrmTeamBoardScreen />} />
            {/* The link in a time-off email, which names the job rather than the screen. */}
            <Route path="time-off" element={<MovedRoute to="/crm/team-board#time-off" />} />
            <Route path="leads" element={<AdminDashboard embedded />} />
            <Route path="help" element={<AdminHelp embedded />} />
            <Route path="*" element={<NotFound embedded homeHref="/crm" homeLabel="Back to CRM overview" />} />
          </Route>

          {/* Catch-all — must stay last in this route group so every named
              route above gets first pick; only unmatched paths fall through
              here. Without this, a bad/old link silently rendered nothing:
              a blank page with a 200 status. */}
          <Route path="*" element={<NotFound />} />
        </Route>

        {/* Slug-scoped tenant surfaces — every /t/:slug/* branch loads
            THAT tenant's brand into its own BrandProvider so multiple
            tenants can be previewed side-by-side. */}
        <Route
          path="/t/:slug/*"
          element={
            <TenantScope>
              <TenantRoutes />
            </TenantScope>
          }
        />
      </Routes>
      </Suspense>
      </VisitInterestProvider>
      <Toaster position="top-right" richColors />
    </>
  );
}


export default function App() {
  return (
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  );
}

/**
 * BrandedOrFoundation — for bare (no-prefix) routes that need different
 * content depending on which hostname resolved: a customer's own verified
 * custom domain should show their agency page, while Foundation's own
 * domain (or any unresolved hostname, which falls back to DEFAULT_TENANT_SLUG
 * "foundation") should keep showing Foundation's marketing page.
 */
/**
 * Which home page this host gets — the agency's own site, or Foundation's.
 *
 * Nothing renders until the brand call has settled. It used to fall through
 * to `foundation` while the slug was still null, which is true on EVERY first
 * paint — so loading an agency's own domain painted Foundation's marketing
 * homepage first and swapped a moment later. On a fast connection that is a
 * flicker; on mobile it is a second or more of the software vendor's website
 * sitting on the customer's domain, which is the opposite of white-label.
 *
 * A blank moment is the right trade. It is imperceptible when the call is
 * quick, and when it is slow the alternative was showing the wrong company.
 */
function BrandedOrFoundation({ tenant, foundation }) {
  const { tenant_slug, brand_resolved } = useBrand();
  if (!brand_resolved) return <div style={{ minHeight: "100vh" }} />;
  return tenant_slug && tenant_slug !== "foundation" ? tenant : foundation;
}

/**
 * ValuationRoute — US tenants get the CMA-request flow in place of the UK
 * instant-AVM tool, which is bound to HM Land Registry / EPC data with no
 * US equivalent. UK tenants keep the instant valuation.
 */
function ValuationRoute() {
  const market = useMarket();
  return market === "us" ? <CmaRequest /> : <Valuation />;
}

/** Wraps the app-level default-brand routes in a single BrandProvider. */
function AppShell() {
  return (
    <BrandProvider>
      {/* INSIDE the provider, and above every page.
          It was rendered from SiteFooter, "so every tenant page gets it
          for free", until the Estate design arrived with a footer of its
          own and the beacon stopped: page_analytics recorded nothing after
          30 September 2026 and nine days of nginx logs carry not one
          request to the endpoint, so every agency's "website views" read
          zero for a fortnight.
          Hoisting it to the root of AppRoutes fixed the footer problem and
          broke a different one, which is why it is here instead: the brand
          provider is a route element, so a tracker above it reads the
          context DEFAULT, finds no tenant slug and skips every page. The
          same trap VisitInterestProvider fell into, from the other side. */}
      <PageviewTracker />
      <WebChat />
      <div className="App">
        <Outlet />
      </div>
    </BrandProvider>
  );
}

/** Tenant-scoped routes rendered inside a per-tenant BrandProvider. */
function TenantRoutes() {
  return (
    // The visit's interest is held once, at the root of AppRoutes, so the
    // slug-scoped preview and an agency's own domain behave identically.
    <div className="App">
      {/* The preview path has its own BrandProvider around it, so this is
          the second of the two places a public page can be rendered from.
          Both are layouts; neither is a footer. */}
      <PageviewTracker />
      <WebChat />
      <Routes>
        <Route index element={<AgencySite />} />
        <Route path="try" element={<Landing />} />
        <Route path="home" element={<AgencySite />} />
        {/* The calculators, in the agency's brand. The home page links here,
            and without these routes the link fell through to Foundation's own
            /calculators and took the visitor off the agency's site. */}
        <Route path="calculators" element={<CalculatorsIndex />} />
        <Route path="calculators/stamp-duty" element={<StampDutyCalculator />} />
        <Route path="calculators/mortgage" element={<MortgageCalculator />} />
        <Route path="calculators/yield" element={<YieldCalculator />} />
        <Route path="calculators/btl-roi" element={<BtlRoiCalculator />} />
        <Route path="calculators/moving-costs" element={<MovingCostsCalculator />} />
        <Route path="valuation" element={<ValuationRoute />} />
        <Route path="market-report" element={<MarketReport />} />
        <Route path="embed" element={<Valuation />} />
        <Route path="register" element={<RegisterApplicant />} />
        {/* Online booking. `book` is the public page; `booking/:token` is
            the link in the confirmation email, which is why it is not
            behind the same guard and why it is noindex. */}
        <Route path="book" element={<BookAppointment />} />
        <Route path="booking/:token" element={<ManageBooking />} />
        <Route path="embed/register" element={<RegisterApplicant />} />
        <Route path="embed/listings" element={<PublicListings />} />
        <Route path="properties" element={<PublicListings />} />
        <Route path="properties/:id" element={<PublicListingDetail />} />

        {/* Foundation Reach — one SEO landing page per area served,
            generated from Brand → Areas served. */}
        <Route path="area/:area" element={<AreaLandingPage />} />
        <Route path="area/:area/house-prices" element={<AreaPricesPage />} />

        {/* Client portals — vendor / landlord / buyer / tenant self-service.
            Inherit tenant brand from the outer TenantScope. */}
        <Route path="portal" element={<PortalLogin />} />
        <Route path="portal/consume/:token" element={<PortalConsume />} />
        <Route path="portal/dashboard" element={<PortalDashboard />} />

        {/* Foundation Sites Phase 2 — About + Contact sub-pages. */}
        <Route path="about" element={<AgencyAbout />} />
        <Route path="contact" element={<AgencyContact />} />
        <Route path="sell" element={<AgencySell />} />
        <Route path="let" element={<AgencyLet />} />
        <Route path="guides" element={<AgencyGuides />} />
        <Route path="guides/:guideSlug" element={<AgencyGuideDetail />} />
        <Route path="fees" element={<AgencyFees />} />
        <Route path="terms" element={<AgencyLegal kind="terms" />} />
        <Route path="privacy" element={<AgencyLegal kind="privacy" />} />
        <Route path="cookies" element={<AgencyLegal kind="cookies" />} />
        <Route path="*" element={<TenantNotFound />} />
      </Routes>
    </div>
  );
}

/** 404 for a tenant's own site — same chrome (header/footer) as every
    other tenant page, "back home" resolves to that tenant's own root. */
function TenantNotFound() {
  const { tenant_slug } = useBrand();
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SiteHeader />
      <div className="flex-1">
        <NotFound embedded homeHref={tenant_slug ? `/t/${tenant_slug}` : "/"} homeLabel="Back home" />
      </div>
      <SiteFooter />
    </div>
  );
}

/**
 * AdminScoped — wraps /admin/* in a BrandProvider.
 *
 * - Before login (no session in localStorage): show Foundation branding
 *   so the sign-in page reads as "Foundation" rather than defaulting to
 *   the showcase tenant.
 * - After login: read the slug from the login response stashed in
 *   localStorage so the tenant's brand (logo, colours, phone) drives
 *   the admin/CRM chrome.
 */
function AdminScoped({ children }) {
  // From the SESSION, not from whatever `wb_admin_tenant` happens to hold.
  // Sign-out used to clear the token and leave the slug, so the next person
  // to sign in on that browser was shown the previous agency's name, logo
  // and phone number over their own account. A slug with no token behind it
  // is not a session, it is a leftover.
  const slug = sessionTenant()?.slug || null;
  // No session, so the Foundation product brand, which is what a login
  // screen and a fresh signup should be wearing anyway.
  return <BrandProvider slug={slug || "foundation"}>{children}</BrandProvider>;
}
